import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBranchHierarchyThunk } from "../../../store/thunks/branchThunks";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  StarIcon,
} from "@heroicons/react/outline";

const BranchHierarchy = () => {
  const dispatch = useDispatch();
  const { branchHierarchy, loading } = useSelector((state) => state.branch);
  const [expandedNodes, setExpandedNodes] = React.useState({});

  useEffect(() => {
    dispatch(getBranchHierarchyThunk());
  }, [dispatch]);

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const renderBranchNode = (branch, level = 0) => {
    const hasChildren = branch.children && branch.children.length > 0;
    const isExpanded = expandedNodes[branch._id];

    return (
      <div key={branch._id} className="select-none">
        <div
          className={`flex items-center py-2 px-3 hover:bg-gray-50 cursor-pointer rounded ${
            level > 0 ? "ml-" + level * 6 : ""
          }`}
          onClick={() => hasChildren && toggleNode(branch._id)}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDownIcon className="h-4 w-4 mr-2 text-gray-500" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 mr-2 text-gray-500" />
            )
          ) : (
            <div className="w-6 mr-2" />
          )}

          <div className="flex items-center flex-1">
            <div
              className={`p-2 rounded-lg mr-3 ${
                branch.isMainBranch ? "bg-yellow-100" : "bg-gray-100"
              }`}
            >
              {branch.isMainBranch && (
                <StarIcon className="h-4 w-4 text-yellow-600" />
              )}
            </div>

            <div className="flex-1">
              <div className="font-medium">{branch.name}</div>
              <div className="text-sm text-gray-500">
                {branch.code} • {branch.address?.city}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  branch.rateConfig?.rateMode === "LIVE"
                    ? "bg-green-100 text-green-700"
                    : branch.rateConfig?.rateMode === "HYBRID"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {branch.rateConfig?.rateMode || "ADMIN"}
              </span>

              {branch.hasStores && (
                <span className="text-xs text-gray-500">
                  {branch.storeCount} stores
                </span>
              )}
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {branch.children.map((child) => renderBranchNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold mb-4">Branch Hierarchy</h2>

      {branchHierarchy ? (
        <div className="space-y-1">{renderBranchNode(branchHierarchy)}</div>
      ) : (
        <div className="text-center text-gray-500 py-8">No branches found</div>
      )}
    </div>
  );
};

export default BranchHierarchy;
